import { useState, useRef } from "react";
import { useEffect } from "react";
import { DataTable } from "../../components/DataTable";
import { useAuth } from "../../context/AuthContext";
import { brandService } from "../../services/brandService";
import {
  Brand,
  CreateBrandRequest
} from "../../interfaces/Brand";
// type Brand = {
//   id: string;
//   name: string;
//   slug: string;
//   website: string;
//   description: string;
//   brandLogo: string;
//   brandBanner: string;
//   contactEmail: string;
//   contactNumber: string;
//   displayOrder: number;
//   status: "Active" | "Hidden";
//   featured: string;
//   metaTitle: string;
//   metaDescription: string;
//   metaKeywords: string;
//   canonicalUrl: string;
//   ogTitle: string;
//   ogDescription: string;
//   ogImage: string;
//   createdAt: string;
// };

function ImageUploadField({
  label,
  hint,
  value,
  onChange,
  previewStyle,
}: {
  label: string;
  hint?: string;
  value: File | null;
  onChange: (v: File | null) => void;
  previewStyle?: React.CSSProperties;
}) {
  const fileRef = useRef<HTMLInputElement>(null);

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    onChange(file);
  }

  return (
    <div>
      <label className="form-label fw-semibold">{label}</label>

      <div className="d-flex gap-2 align-items-center">

        {/* PREVIEW */}
        {value ? (
          <img
            src={URL.createObjectURL(value)}
            style={previewStyle}
          />
        ) : (
          <div className="text-muted">No image selected</div>
        )}

        {/* BUTTON */}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => fileRef.current?.click()}
        >
          Upload
        </button>

        {/* FILE INPUT */}
        <input
          ref={fileRef}
          type="file"
          className="d-none"
          accept="image/*"
          onChange={handleFile}
        />
      </div>

      {hint && <small className="text-muted">{hint}</small>}
    </div>
  );
}

export function CreateBrandPage() {
  const API_PATH =
    process.env.REACT_APP_API_PATH;

  const { user } = useAuth();
  const canManage = user?.role === "admin";

  const [brandName, setName] = useState<string>("");
  const [brandSlug, setSlug] = useState("");
  const [websiteURL, setWebsite] = useState("");
  const [description, setDescription] = useState("");


  const [contactEmail, setContactEmail] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [displayOrder, setDisplayOrder] = useState<number>(0);
  const [status, setStatus] = useState<"Active" | "Hidden">("Active");
  const [isFeatured, setFeatured] = useState(false);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDesc] = useState("");
  const [metaKeywords, setMetaKeys] = useState("");
  const [canonicalUrl, setCanonicalUrl] = useState("");
  const [ogTitle, setOgTitle] = useState("");
  const [ogDescription, setOgDesc] = useState("");
  const [brandLogo, setBrandLogo] = useState<File | null>(null);
  const [brandImage, setBrandImage] = useState<File | null>(null);
  const [ogImage, setOgImage] = useState<File | null>(null);

  const [saved, setSaved] = useState(false);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<Brand>>({});
  const [schemaMarkup, setSchemaMarkup] = useState("");
  const [editingBrandId, setEditingBrandId] = useState<number | null>(null);

  const [viewBrand, setViewBrand] =
    useState<Brand | null>(null);

  const [deleteConfirmId, setDeleteConfirmId] =
    useState<number | null>(null);


  const [infoModal, setInfoModal] = useState<{ msg: string; type: "success" | "error" | "warn" } | null>(null);
  function handleNameChange(val: string) {
    setName(val);
    setSlug(val.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""));
  }

  function resetForm() {
    setName("");
    setSlug("");
    setWebsite("");
    setDescription("");
    setStatus("Active");
    setFeatured(false);

    setBrandLogo(null);
    setBrandImage(null);
    setOgImage(null);

    setContactEmail("");
    setContactNumber("");
    setDisplayOrder(0);

    setMetaTitle("");
    setMetaDesc("");
    setMetaKeys("");
    setCanonicalUrl("");

    setOgTitle("");
    setOgDesc("");
  }
  function editBrand(item: Brand) {

    setEditingBrandId(item.brandID);

    setName(item.brandName);

    setSlug(item.brandSlug);

    setDescription(item.description || "");

    setWebsite(item.websiteURL || "");

    setContactEmail(item.contactEmail || "");

    setContactNumber(item.contactNumber || "");

    setDisplayOrder(item.displayOrder || 0);

    setStatus(
      (item.status as "Active" | "Hidden") || "Active"
    );

    setFeatured(item.isFeatured || false);

    setMetaTitle(item.metaTitle || "");

    setMetaDesc(item.metaDescription || "");

    setMetaKeys(item.metaKeywords || "");

    setCanonicalUrl(item.canonicalUrl || "");

    setOgTitle(item.ogTitle || "");

    setOgDesc(item.ogDescription || "");

    setSchemaMarkup(item.schemaMarkup || "");

    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }


  useEffect(() => {
    loadBrands();
  }, []);
  async function loadBrands() {
    try {
      const response = await brandService.getAll();
     // console.log(response, "*******");
      setBrands(response);

    } catch (error: any) {
      console.error(error);
      setInfoModal({ msg: error.message || "Failed to load brands", type: "error" });
    }
  }

  async function handleSubmit() {
    if (!brandName.trim()) return;

    // ========================= // DUPLICATE BRAND VALIDATION // =========================
    if (!brandService.validateDuplicateBrand(brands, brandName, editingBrandId)) { setInfoModal({ msg: "Brand already exists", type: "warn" }); return; }
    try {
      const payload: CreateBrandRequest = {
        brandName,
        brandSlug,

        description,
        websiteURL,
        contactEmail,
        contactNumber,

        isFeatured,
        displayOrder,
        status,
        metaTitle,
        metaDescription,
        metaKeywords,
        canonicalUrl,

        ogTitle,
        ogDescription,
        schemaMarkup,

        brandLogo,
        brandImage,
        ogImage,
      };

      let response;

      // ======================================
      // CREATE OR UPDATE
      // ======================================

      if (editingBrandId !== null) {
        response = await brandService.update({
          brandID: editingBrandId,
          ...payload,
        });
      } else {
        response = await brandService.create(payload);
      }
  
      setInfoModal({ msg: response.message || "Brand saved successfully", type: "success" });

      await loadBrands();
      resetForm();
      setEditingBrandId(null);
    } catch (error: any) {
      console.error(error);
      setInfoModal({ msg: error.message || "Error saving brand", type: "error" });
    }
  }

  async function deleteBrand() {

    if (!deleteConfirmId) {
      return;
    }

    try {

      const response =
        await brandService.delete(
          deleteConfirmId
        );

      setInfoModal({ msg: response.message, type: "success" });

      await loadBrands();

      setDeleteConfirmId(null);

    } catch (error) {

      console.error(error);

      setInfoModal({ msg: "Error deleting brand", type: "error" });
    }
  }
  function startEdit(b: Brand) {
    setEditingId(b.brandID);
    setEditForm({ ...b });
  }
  // function saveEdit(id: string) {
  //   setBrands(prev => prev.map(b => b.id === id ? { ...b, ...editForm } as Brand : b));
  //   setEditingId(null); setEditForm({});
  // }
  function cancelEdit() { setEditingId(null); setEditForm({}); }
  async function toggleStatus(
    item: Brand
  ) {

    try {

      await brandService.update({

        brandID: item.brandID,

        brandName: item.brandName,

        brandSlug: item.brandSlug,

        description: item.description,

        websiteURL: item.websiteURL,

        contactEmail: item.contactEmail,

        contactNumber: item.contactNumber,

        isFeatured: item.isFeatured,

        displayOrder: item.displayOrder,

        status:
          item.status === "Active"
            ? "Hidden"
            : "Active",

        metaTitle: item.metaTitle,

        metaDescription: item.metaDescription,

        metaKeywords: item.metaKeywords,

        canonicalUrl: item.canonicalUrl,

        ogTitle: item.ogTitle,

        ogDescription: item.ogDescription,

        schemaMarkup: item.schemaMarkup
      });

      await loadBrands();

    } catch (error) {

      console.error(error);
    }
  }

  return (
    <div className="d-grid gap-4">
      <div className="card border-0 shadow-sm mst-card">
        <div className="card-body p-4">
          <div className="d-flex align-items-center gap-2 mb-4">
            <div className="mst-icon-box"><i className="bi bi-award"></i></div>
            <div>
              <h5 className="mb-0 fw-bold mst-title">Create Brand</h5>
              <small className="mst-subtitle">Add a new jewellery brand to the catalogue</small>
            </div>
          </div>

          {saved && (
            <div className="alert alert-success d-flex align-items-center gap-2 py-2 mb-3 mst-alert">
              <i className="bi bi-check-circle-fill"></i> Brand saved successfully!
            </div>
          )}

          <div className="row g-3">

            {/* ── Basic Info ── */}
            <div className="col-12">
              <div className="mst-seo-divider"><i className="bi bi-info-circle me-2"></i>Basic Information</div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Brand Name <span className="text-danger">*</span></label>
              <input className="form-control mst-control" placeholder="e.g. Tanishq"
                value={brandName} onChange={e => handleNameChange(e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Brand Slug (auto-generated)</label>
              <div className="input-group">
                <span className="input-group-text mst-ig-text">/brand/</span>
                <input className="form-control mst-control-right" placeholder="tanishq"
                  value={brandSlug} onChange={e => setSlug(e.target.value)} />
              </div>
            </div>
            <div className="col-md-8">
              <label className="form-label fw-semibold mst-label">Website URL</label>
              <input className="form-control mst-control" placeholder="https://example.com"
                value={websiteURL} onChange={e => setWebsite(e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Status</label>
              <select className="form-select mst-select" value={status}
                onChange={e => setStatus(e.target.value as "Active" | "Hidden")}>
                <option value="Active">Active</option>
                <option value="Hidden">Hidden</option>
              </select>
            </div>
            <div className="col-md-5">
              <label className="form-label fw-semibold mst-label">Contact Email</label>
              <input type="email" className="form-control mst-control" placeholder="brand@example.com"
                value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
            </div>

            <div className="col-md-5">

              <label className="form-label fw-semibold mst-label">
                Contact Number
              </label>

              <input
                className="form-control mst-control"
                placeholder="9876543210"
                value={contactNumber}

                maxLength={10}

                onChange={(e) => {

                  // only numbers allowed
                  const value =
                    e.target.value.replace(/\D/g, "");

                  // max 10 digits
                  setContactNumber(
                    value.slice(0, 10)
                  );
                }}
              />

            </div>


            <div className="col-md-2">
              <label className="form-label fw-semibold mst-label">Display Order</label>
              <input type="number" className="form-control mst-control" placeholder="0" min={0}
                value={displayOrder || ""} onChange={e => setDisplayOrder(Number(e.target.value))} />
            </div>
            <div className="col-12">
              <label className="form-label fw-semibold mst-label">Description</label>
              <textarea className="form-control mst-textarea" placeholder="About this brand..." rows={3}
                value={description} onChange={e => setDescription(e.target.value)} />
            </div>

            {/* ── Brand Logo ── */}
            <div className="col-md-6">
              <ImageUploadField
                label="Brand Logo"
                hint="PNG/SVG with transparent background recommended"
                value={brandLogo}
                onChange={setBrandLogo}
                previewStyle={{ height: 60, objectFit: "contain", borderRadius: 8, background: "#f9f5f0", padding: 4 }}
              />
            </div>

            {/* ── Brand Banner ── */}
            <div className="col-md-6">
              <ImageUploadField
                label="Brand Banner"
                hint="Recommended: 1200×300px"
                value={brandImage}
                onChange={setBrandImage}
                previewStyle={{ height: 60, objectFit: "cover", borderRadius: 8, width: "100%" }}
              />
            </div>

            {/* ── Featured ── */}
            <div className="col-12">
              <div className="d-flex align-items-center gap-3 p-3 mst-featured-row">
                <div className="form-check form-switch mb-0">
                  <input className="form-check-input mst-featured-switch" type="checkbox" id="featuredSwitch"
                    checked={isFeatured} onChange={e => setFeatured(e.target.checked)} />
                  <label className="form-check-label fw-semibold ms-2 mst-featured-label" htmlFor="featuredSwitch">
                    Mark as Featured Brand
                  </label>
                </div>
                <small className="mst-featured-hint">Featured brands appear on the homepage</small>
              </div>
            </div>

            {/* ── SEO ── */}
            <div className="col-12 mt-2">
              <div className="mst-seo-divider"><i className="bi bi-search me-2"></i>SEO & Meta Settings</div>
            </div>
            <div className="col-12">
              <div className="mst-seo-hint"><i className="bi bi-info-circle me-1"></i>These fields control how this brand page appears in Google search results and social media.</div>
            </div>
            <div className="col-md-8">
              <label className="form-label fw-semibold mst-label">Meta Title <span className="mst-seo-counter">{metaTitle.length}/60</span></label>
              <input className="form-control mst-control" placeholder="e.g. Tanishq Jewellery | NineSisters" maxLength={60}
                value={metaTitle} onChange={e => setMetaTitle(e.target.value)} />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold mst-label">Meta Keywords</label>
              <input className="form-control mst-control" placeholder="e.g. tanishq, gold jewellery"
                value={metaKeywords} onChange={e => setMetaKeys(e.target.value)} />
            </div>
            <div className="col-12">
              <label className="form-label fw-semibold mst-label">Meta Description <span className="mst-seo-counter">{metaDescription.length}/160</span></label>
              <textarea className="form-control mst-textarea" placeholder="Brief summary shown in search results — 120–160 characters recommended" rows={2} maxLength={160}
                value={metaDescription} onChange={e => setMetaDesc(e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">Canonical URL <span className="mst-seo-opt">(optional)</span></label>
              <input className="form-control mst-control" placeholder="https://ninesistersjewels.com/brand/..."
                value={canonicalUrl} onChange={e => setCanonicalUrl(e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">OG Title <span className="mst-seo-opt">(optional)</span></label>
              <input className="form-control mst-control" placeholder="e.g. Shop Tanishq | NineSisters"
                value={ogTitle} onChange={e => setOgTitle(e.target.value)} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold mst-label">OG Description <span className="mst-seo-opt">(optional)</span></label>
              <input className="form-control mst-control" placeholder="Social media preview description"
                value={ogDescription} onChange={e => setOgDesc(e.target.value)} />
            </div>

            {/* ── OG Image with file upload ── */}
            <div className="col-md-6">
              <ImageUploadField
                label="OG Image (Social share image)"
                hint="Recommended: 1200×630px"
                value={ogImage}
                onChange={setOgImage}
                previewStyle={{ height: 60, objectFit: "cover", borderRadius: 8, width: "100%" }}
              />
            </div>
          </div>

          <div className="d-flex gap-2 mt-4">
            <button type="button" className="btn btn-primary px-4 mst-btn-save"
              onClick={handleSubmit} disabled={!brandName.trim()}>
              <i className={`bi ${editingBrandId !== null
                  ? "bi-pencil-square"
                  : "bi-plus-circle"
                } me-2`}></i>

              {editingBrandId !== null
                ? "Update Brand"
                : "Save Brand"}
            </button>
            <button type="button" className="btn mst-btn-reset" onClick={resetForm}>
              Reset
            </button>
          </div>
        </div>
      </div>

      <DataTable<Brand>
        title="Brands"
        rows={brands}
        rowKey={r => r.brandID.toString()}
        searchPlaceholder="Search brands..."
        exportFileName="brands"
        columns={[
          {
            key: "name", label: "Brand", sortable: true,
            render: r => editingId === r.id
              ? <input className="form-control form-control-sm" value={editForm.brandName ?? ""} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
              : (
                <div className="d-flex align-items-center gap-2">
                  {r.brandLogo && (
                    <img src={`${API_PATH}/uploads/${r.brandLogo}`} alt={r.brandName}
                      style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 6, background: "#f9f5f0", padding: 2 }} />
                  )}
                  <span className="fw-semibold">{r.brandName}</span>
                </div>
              ),
          },
          { key: "slug", label: "Slug", render: r => <code className="mst-code">{r.brandSlug}</code> },
          {
            key: "website", label: "Website",
            render: r => r.websiteURL
              ? <a href={r.websiteURL} target="_blank" rel="noreferrer" className="mst-link"><i className="bi bi-link-45deg me-1"></i>Visit</a>
              : <span className="mst-smoke">—</span>,
          },
          {
            key: "featured",
            label: "Featured",

            render: r =>

              r.isFeatured
                ? (
                  <span className="badge text-bg-warning">
                    <i className="bi bi-star-fill me-1"></i>
                    Yes
                  </span>
                )
                : (
                  <span className="badge bg-secondary">
                    No
                  </span>
                ),
          },
          {
            key: "status",
            label: "Status",

            render: r => (

              <span
                className={`badge ${r.status
                    ? "bg-success"
                    : "bg-danger"
                  }`}
              >
                {r.status
                  ? "Active"
                  : "Hidden"}
              </span>
            ),
          },
          {
            key: "createdDate",
            label: "Created",
            sortable: true,

            render: r =>

              r.createdDate
                ? new Date(r.createdDate)
                  .toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric"
                  })
                : "-"
          },
          {
            key: "actions",
            label: "Actions",

            render: (r) => (

              <div className="d-flex gap-2">

                {/* VIEW */}
                <button
                  className="btn btn-info btn-sm"
                  onClick={() => setViewBrand(r)}
                >
                  <i className="bi bi-eye"></i>
                </button>

                {/* EDIT */}
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => editBrand(r)}
                >
                  <i className="bi bi-pencil-square"></i>
                </button>

                {/* TOGGLE */}
                <button
                  className="btn btn-warning btn-sm"
                  onClick={() => toggleStatus(r)}
                >
                  Toggle
                </button>

                {/* DELETE */}
                {Boolean(r.isActive) && (

                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() =>
                      setDeleteConfirmId(
                        r.brandID
                      )
                    }
                  >
                    <i className="bi bi-trash"></i>
                  </button>

                )}

              </div>
            )
          }
        ]}
      />
      {deleteConfirmId && (

        <div
          className="pm-del-overlay"
          onClick={(e) => {

            if (e.target === e.currentTarget) {

              setDeleteConfirmId(null);
            }
          }}
        >

          <div className="pm-del-box">

            <div className="pm-del-ico">

              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2.5"
              >
                <polyline points="3 6 5 6 21 6" />

                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />

                <path d="M10 11v6" />

                <path d="M14 11v6" />
              </svg>

            </div>

            <div className="pm-del-title">
              Delete Brand?
            </div>

            <div className="pm-del-sub">
              This action cannot be undone.
            </div>

            <div className="pm-del-btns">

              <button
                className="pm-btn-ghost"
                onClick={() =>
                  setDeleteConfirmId(null)
                }
                type="button"
              >
                Cancel
              </button>

              <button
                className="pm-btn-danger"
                onClick={deleteBrand}
                type="button"
              >
                Delete
              </button>

            </div>

          </div>

        </div>
      )}
      {viewBrand && (

        <div
          className="pm-del-overlay"
          onClick={(e) => {

            if (e.target === e.currentTarget) {

              setViewBrand(null);
            }
          }}
        >

          <div
            className="pm-del-box"
            style={{
              maxWidth: "700px",
              width: "100%"
            }}
          >

            <div className="d-flex justify-content-between align-items-center mb-3">

              <h4 className="mb-0">
                Brand Details
              </h4>

              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setViewBrand(null)}
              >
                Close
              </button>

            </div>

            {/* LOGO */}
            {viewBrand.brandLogo && (

              <div className="mb-3">

                <img
                  src={`${API_PATH}/uploads/${viewBrand.brandLogo}`}
                  alt={viewBrand.brandName}
                  style={{
                    width: 100,
                    height: 100,
                    objectFit: "contain",
                    borderRadius: 10,
                    border: "1px solid #ddd",
                    padding: 5
                  }}
                />

              </div>
            )}

            <div className="row g-3">

              <div className="col-md-6">
                <strong>Name:</strong>
                <div>{viewBrand.brandName}</div>
              </div>

              <div className="col-md-6">
                <strong>Slug:</strong>
                <div>{viewBrand.brandSlug}</div>
              </div>

              <div className="col-md-6">
                <strong>Website:</strong>

                <div>
                  {viewBrand.websiteURL || "-"}
                </div>
              </div>

              <div className="col-md-6">
                <strong>Email:</strong>

                <div>
                  {viewBrand.contactEmail || "-"}
                </div>
              </div>

              <div className="col-md-6">
                <strong>Phone:</strong>

                <div>
                  {viewBrand.contactNumber || "-"}
                </div>
              </div>

              <div className="col-md-6">
                <strong>Featured:</strong>

                <div>
                  {viewBrand.isFeatured
                    ? "Yes"
                    : "No"}
                </div>
              </div>

              <div className="col-md-6">
                <strong>Status:</strong>

                <div>
                  <span
                    className={`badge ${viewBrand.status
                        ? "bg-success"
                        : "bg-danger"
                      }`}
                  >
                    {viewBrand.status
                      ? "Active"
                      : "Hidden"}
                  </span>
                </div>
              </div>

              <div className="col-md-6">
                <strong>Display Order:</strong>

                <div>
                  {viewBrand.displayOrder}
                </div>
              </div>

              <div className="col-12">
                <strong>Description:</strong>

                <div>
                  {viewBrand.description || "-"}
                </div>
              </div>

              <div className="col-12">
                <strong>Meta Title:</strong>

                <div>
                  {viewBrand.metaTitle || "-"}
                </div>
              </div>

              <div className="col-12">
                <strong>Meta Description:</strong>

                <div>
                  {viewBrand.metaDescription || "-"}
                </div>
              </div>

            </div>

          </div>

        </div>
      )}

      {/* ── Info / Success / Error Modal ── */}
      {infoModal && (
        <div className="pm-del-overlay" onClick={() => setInfoModal(null)}>
          <div className="pm-del-box">
            <div className="pm-del-ico" style={{
              background: infoModal.type === "success" ? "#f0fdf4" : infoModal.type === "warn" ? "#fefce8" : "#fef2f2"
            }}>
              {infoModal.type === "success" && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
              )}
              {infoModal.type === "warn" && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
              )}
              {infoModal.type === "error" && (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
              )}
            </div>
            <div className="pm-del-title" style={{
              color: infoModal.type === "success" ? "#166534" : infoModal.type === "warn" ? "#854d0e" : "#991b1b"
            }}>
              {infoModal.msg}
            </div>
            <div className="pm-del-btns">
              <button className="pm-btn-ghost" onClick={() => setInfoModal(null)} type="button">OK</button>
            </div>
          </div>
        </div>
      )}
    </div>

  );
}